import { DomainAddressType } from "@porchpass/api-client";
import type { HomeFieldsFormData } from "@porchpass/ui";
import type {
  HomeDownPaymentFormData,
  HomeFormData,
  HomeManufacturerDetailsFormData,
  HomePlacementFormData,
} from "src/types/home";
import { toUiAddress, fromUiAddressPatch } from "./address";

export function toHomeUsageFormData(dto: HomeFormData): HomeFieldsFormData {
  return {
    homeType: "",
    primaryUse: dto.usage_type ?? "",
    usageTypeOther: dto.usage_type_other,
  };
}

export function fromHomeUsageFormData(
  data: HomeFieldsFormData,
): Partial<HomeFormData> {
  return {
    usage_type: data.primaryUse || undefined,
    usage_type_other: data.usageTypeOther,
  };
}

export function toHomePlacementFormData(
  dto: HomePlacementFormData,
): HomeFieldsFormData {
  return {
    homeType: "",
    primaryUse: "",
    propertyAddress: toUiAddress(dto, DomainAddressType.AddressHomePlacement),
  };
}

export function fromHomePlacementFormData(
  data: HomeFieldsFormData,
): Partial<HomePlacementFormData> {
  if (!data.propertyAddress) return {};
  return fromUiAddressPatch(data.propertyAddress);
}

export function toHomeManufactureFormData(
  dto: HomeManufacturerDetailsFormData,
): HomeFieldsFormData {
  return {
    homeType: dto.section_type ?? "",
    homeTypeOther: dto.section_type_other,
    primaryUse: "",
    manufacturer: dto.manufacturer_type,
    manufacturerOther: dto.manufacturer_type_other,
    modelName: dto.model_name,
    modelYear: dto.model_year ? Number(dto.model_year) : undefined,
  };
}

export function fromHomeManufactureFormData(
  data: HomeFieldsFormData,
): Partial<HomeManufacturerDetailsFormData> {
  return {
    manufacturer_type: (data.manufacturer || undefined) as
      | HomeManufacturerDetailsFormData["manufacturer_type"]
      | undefined,
    manufacturer_type_other: data.manufacturerOther,
    model_name: data.modelName,
    model_year: data.modelYear != null ? String(data.modelYear) : undefined,
    section_type: data.homeType || undefined,
    section_type_other: data.homeTypeOther,
  };
}

function toUnsetAmountNumber(value?: string): number | undefined {
  if (value == null || value === "") return undefined;
  const num = Number(value);
  if (Number.isNaN(num) || num === 0) return undefined;
  return num;
}

function toAmountNumber(value?: string): number | undefined {
  if (value == null || value === "") return undefined;
  const num = Number(value);
  if (Number.isNaN(num)) return undefined;
  return num;
}

export function toHomeDownPaymentFormData(
  dto: HomeDownPaymentFormData,
): HomeFieldsFormData {
  return {
    homeType: "",
    primaryUse: "",
    homePrice: toUnsetAmountNumber(dto.price),
    upgradesCost: toUnsetAmountNumber(dto.upgrades_cost),
    siteImprovementCost: toUnsetAmountNumber(dto.site_improvement_cost),
    downPaymentAmount: toAmountNumber(dto.down_payment_amount),
    downPaymentSource: dto.down_payment_source_type,
    downPaymentSourceOther: dto.down_payment_source_other,
    downPaymentGiftorName: dto.down_payment_giftor_name,
  };
}

export function fromHomeDownPaymentFormData(
  data: HomeFieldsFormData,
): Partial<HomeDownPaymentFormData> {
  return {
    price: data.homePrice != null ? String(data.homePrice) : undefined,
    upgrades_cost:
      data.upgradesCost != null ? String(data.upgradesCost) : undefined,
    site_improvement_cost:
      data.siteImprovementCost != null
        ? String(data.siteImprovementCost)
        : undefined,
    down_payment_amount:
      data.downPaymentAmount != null
        ? String(data.downPaymentAmount)
        : undefined,
    down_payment_source_type: data.downPaymentSource || undefined,
    down_payment_source_other: data.downPaymentSourceOther,
    down_payment_giftor_name: data.downPaymentGiftorName,
  };
}

export function homeUsageErrorsAfterSubmit(
  formErrors: { usage_type_other?: { message?: string } },
  hasAttemptedFormSubmission: boolean,
) {
  if (!hasAttemptedFormSubmission) return {};
  return {
    usageTypeOther: formErrors.usage_type_other?.message,
  };
}

export function homeManufactureErrorsAfterSubmit(
  formErrors: {
    manufacturer_type?: { message?: string };
    manufacturer_type_other?: { message?: string };
    model_name?: { message?: string };
    model_year?: { message?: string };
    section_type?: { message?: string };
    section_type_other?: { message?: string };
  },
  hasAttemptedFormSubmission: boolean,
) {
  if (!hasAttemptedFormSubmission) return {};
  return {
    manufacturer: formErrors.manufacturer_type?.message,
    manufacturerOther: formErrors.manufacturer_type_other?.message,
    modelName: formErrors.model_name?.message,
    modelYear: formErrors.model_year?.message,
    homeType: formErrors.section_type?.message,
    homeTypeOther: formErrors.section_type_other?.message,
  };
}

export function homeDownPaymentErrorsAfterSubmit(
  formErrors: {
    price?: { message?: string };
    upgrades_cost?: { message?: string };
    site_improvement_cost?: { message?: string };
    down_payment_amount?: { message?: string };
    down_payment_source_type?: { message?: string };
    down_payment_source_other?: { message?: string };
    down_payment_giftor_name?: { message?: string };
  },
  hasAttemptedFormSubmission: boolean,
) {
  if (!hasAttemptedFormSubmission) return {};
  return {
    homePrice: formErrors.price?.message,
    upgradesCost: formErrors.upgrades_cost?.message,
    siteImprovementCost: formErrors.site_improvement_cost?.message,
    downPaymentAmount: formErrors.down_payment_amount?.message,
    downPaymentSource: formErrors.down_payment_source_type?.message,
    downPaymentSourceOther: formErrors.down_payment_source_other?.message,
    downPaymentGiftorName: formErrors.down_payment_giftor_name?.message,
  };
}
